import * as React from 'react';
import NukaCarousel from 'nuka-carousel';
import styled from 'styled-components';
import { isNot, FixedRatio, media } from '@packages/ui';

import { ModuleCarousel, CmsModuleType, BaseModuleImage, ImageCDNProvider } from '@packages/common';

import { ArrowLeft, ArrowRight, PagingDots } from '../../Carousel';
import { CarouselItemProps, CarouselItem } from '../CarouselItem/CarouselItem';
import { ErrorProps, Error } from '../Error/Error';

export const StyledArrowLeft = styled(ArrowLeft)`
  display: none;
  transition: opacity 0.3s ease-out;

  ${media.from.lg`
    display: block;
    opacity: 0;
  `}
`;

export const StyledArrowRight = styled(ArrowRight)`
  display: none;
  transition: opacity 0.3s ease-out;

  ${media.from.lg`
    display: block;
    opacity: 0;
  `}
`;

export const Container: any = styled.div`
  li {
    /* needed for clickable nuka-slides */
    cursor: auto;
  }

  ${isNot('withoutControls')`
    /* add padding in regards to dot-navigation */
    padding-bottom: 24px;

    ${media.from.md`
      padding-bottom: 35px;
    `}

    ${media.from.lg`
      padding-bottom: 0;
    `}
  `}

  &:hover {
    ${StyledArrowLeft},
    ${StyledArrowRight} {
      opacity: 1;
    }
  }
`;

export const StyledPagingDots = styled(PagingDots)`
  top: 22px;

  ${media.from.md`
    top: 33px;
  `}

  ${media.from.lg`
    display: none;
  `}
`;

const defaultRatio = [1, 1, 0.48, 0.32];
const getRatio = (items: BaseModuleImage[]): number | number[] => {
  const ratio = items?.[0]?.image?.ratio;
  return ratio == null || ratio !== ratio ? defaultRatio : ratio;
};

export type CarouselProps = ModuleCarousel & {
  cdnProvider?: ImageCDNProvider;
};

export class Carousel extends React.Component<CarouselProps> {
  renderItem = (item: Omit<CarouselItemProps, 'cdnProvider'> | ErrorProps) => {
    if (item.contentType === CmsModuleType.ERROR) {
      return <Error key={item.id} {...(item as ErrorProps)} />;
    }
    return <CarouselItem key={item.id} {...(item as CarouselItemProps)} />;
  };

  renderCenterLeftControls: any = (props: any) => <StyledArrowLeft onClick={props.previousSlide}>Prev</StyledArrowLeft>;
  renderBottomCenterControls: any = (props: any) => <StyledPagingDots {...props} />;
  renderCenterRightControls: any = (props: any) => <StyledArrowRight onClick={props.nextSlide}>Next</StyledArrowRight>;

  render() {
    const { items, autoplay, autoplayInterval, wrapAround, withoutControls } = this.props;
    if (items && items.length > 0) {
      return (
        <Container withoutControls={withoutControls}>
          <FixedRatio ratio={getRatio(items)} isHeightLimited={false}>
            {items && items.length > 1 && (
              <NukaCarousel
                autoplay={autoplay}
                wrapAround={wrapAround}
                autoplayInterval={autoplayInterval}
                withoutControls={withoutControls}
                slidesToShow={1}
                renderCenterLeftControls={this.renderCenterLeftControls}
                renderCenterRightControls={this.renderCenterRightControls}
                renderBottomCenterControls={this.renderBottomCenterControls}
              >
                {items.map(this.renderItem)}
              </NukaCarousel>
            )}
            {items && items.length === 1 && this.renderItem(items[0])}
          </FixedRatio>
        </Container>
      );
    }
    return null;
  }
}
